import { useEffect, useRef, useState } from "react";
import { z } from "zod";

export const DemoResetStatusSchema = z.object({
  scheduleTimeZone: z.string().min(1),
  scheduleLocalTime: z.string().min(1),
  scheduleUtcOffset: z.string().min(1),
  nextScheduledResetAt: z.string().datetime(),
  lastSuccessfulResetAt: z.string().datetime().nullable(),
  currentRunStartedAt: z.string().datetime().nullable(),
  state: z.enum(["scheduled", "running", "failed", "overdue", "unavailable"]),
  fixtureVersion: z.string().min(1),
  resetGeneration: z.number().int().nonnegative(),
});
export type DemoResetStatus = z.infer<typeof DemoResetStatusSchema>;

const RESET_GENERATION_KEY = "vote-results:reset-generation";

const formatResetTimestamp = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));

const stateCopy = (status: DemoResetStatus | null, unavailable: boolean) => {
  if (unavailable || status?.state === "unavailable") {
    return "Reset status is unavailable. Treat any displayed demo changes as temporary.";
  }
  if (!status) return "Reading the reset schedule…";
  if (status.state === "running") {
    return "Reset is in progress. Operator writes are temporarily paused.";
  }
  if (status.state === "failed") {
    return "The latest reset failed. Demo changes may remain while recovery is investigated.";
  }
  if (status.state === "overdue") {
    return "The reset is overdue. Demo changes may remain past the scheduled window.";
  }
  return "Reset service is scheduled.";
};

async function fetchResetStatus(signal: AbortSignal): Promise<DemoResetStatus> {
  const response = await fetch("/api/demo-reset-status", {
    headers: { accept: "application/json" },
    cache: "no-store",
    signal,
  });
  const parsed = DemoResetStatusSchema.safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    throw new Error(`Reset status request failed with ${response.status}.`);
  }
  return parsed.data;
}

export const DemoResetNotice = ({ compact = false }: { compact?: boolean }) => {
  const [status, setStatus] = useState<DemoResetStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const initialGeneration = useRef<number | null>(null);
  const sawRunning = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let active = true;

    const poll = async () => {
      try {
        const next = await fetchResetStatus(controller.signal);
        if (!active) return;
        setStatus(next);
        setUnavailable(false);
        sawRunning.current ||= next.state === "running";

        if (initialGeneration.current === null) {
          initialGeneration.current = next.resetGeneration;
          if (next.state !== "running") {
            sessionStorage.setItem(RESET_GENERATION_KEY, String(next.resetGeneration));
          }
        } else {
          const completedNewGeneration =
            next.state !== "running" &&
            next.lastSuccessfulResetAt !== null &&
            (next.resetGeneration !== initialGeneration.current || sawRunning.current);
          if (completedNewGeneration) {
            const applied = sessionStorage.getItem(RESET_GENERATION_KEY);
            if (applied !== String(next.resetGeneration)) {
              sessionStorage.setItem(RESET_GENERATION_KEY, String(next.resetGeneration));
              window.location.reload();
              return;
            }
          }
        }
        timeout = setTimeout(poll, next.state === "running" ? 1_000 : 3_000);
      } catch (error) {
        if (!active || (error instanceof DOMException && error.name === "AbortError")) return;
        setUnavailable(true);
        timeout = setTimeout(poll, 5_000);
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        if (timeout) clearTimeout(timeout);
        void poll();
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    void poll();
    return () => {
      active = false;
      controller.abort();
      if (timeout) clearTimeout(timeout);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  return (
    <aside
      className={compact ? "demo-reset-notice demo-reset-notice--compact" : "demo-reset-notice"}
      aria-label="Demo data reset"
    >
      <div>
        <strong>Demo data reset</strong>
        <span>{stateCopy(status, unavailable)}</span>
      </div>
      <p>
        Demo environment —{" "}
        {status ? (
          <>
            The daily reset starts at{" "}
            <strong>
              {status.scheduleLocalTime} Philippine Time ({status.scheduleTimeZone},{" "}
              {status.scheduleUtcOffset})
            </strong>
            .{" "}
          </>
        ) : (
          "The server schedule is temporarily unavailable. "
        )}
        Do not enter sensitive information.
      </p>
      <dl>
        <div>
          <dt>Next reset:</dt>
          <dd>
            {status ? (
              <time dateTime={status.nextScheduledResetAt}>
                {formatResetTimestamp(status.nextScheduledResetAt)}
              </time>
            ) : (
              "Unavailable"
            )}
          </dd>
        </div>
        <div>
          <dt>Last completed:</dt>
          <dd>
            {status?.lastSuccessfulResetAt ? (
              <time dateTime={status.lastSuccessfulResetAt}>
                {formatResetTimestamp(status.lastSuccessfulResetAt)}
              </time>
            ) : (
              "No completed reset recorded"
            )}
          </dd>
        </div>
      </dl>
    </aside>
  );
};
